import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Grid,
  CircularProgress,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
} from '@mui/material';

import { getApproval, patchApproval, resendApproval, sendApproval } from '../../utils/approvalsApi';
import ApprovalStatusChip from './components/ApprovalStatusChip';
import ApprovalTimeline from './components/ApprovalTimeline';
import ResendConfirmDialog from './components/ResendConfirmDialog';
import { TERMINAL_STATUSES } from './utils/statusTokens';

const POLL_MS = 5000;
const MONO = { fontFamily: 'Roboto Mono, ui-monospace, SFMono-Regular, monospace' };

const formatStamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_e) {
    return '—';
  }
};

const extractErrorMessage = (err) => {
  const data = err?.response?.data;
  if (data?.error) return data.error;
  if (data?.detail) return data.detail;
  if (err?.response?.status === 404) return 'Approval not found.';
  return 'Something went wrong. Try again or contact support.';
};

const ApprovalDetailPage = () => {
  const { id } = useParams();
  const [approval, setApproval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Inline edit-draft state. Local form state is the source of truth while
  // dirty; polled server updates do NOT overwrite unsaved edits (we only
  // hydrate when the user opens the editor).
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState(null);

  const resendButtonRef = useRef(null);

  const approvalId = id;
  const status = approval?.status || null;
  const isExpired = approval?.is_expired === true;
  const isTerminal = status ? TERMINAL_STATUSES.has(status) : false;

  // Initial / refresh fetch — keyed on stable primitives only.
  useEffect(() => {
    if (!approvalId) return undefined;
    let cancelled = false;
    setLoading(true);
    getApproval(approvalId)
      .then((res) => {
        if (cancelled) return;
        setApproval(res?.data || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setApproval(null);
        setError(extractErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [approvalId, refreshKey]);

  // Polling — keyed by approvalId + isTerminal only. Cleared on unmount
  // and on terminal status (signed/declined/expired). No callback deps.
  useEffect(() => {
    if (!approvalId || isTerminal || isExpired) return undefined;
    if (status !== 'sent' && status !== 'viewed') return undefined;
    const interval = window.setInterval(() => {
      getApproval(approvalId)
        .then((res) => setApproval(res?.data || null))
        .catch(() => {
          /* silent on poll errors; surface only on user-driven refresh */
        });
    }, POLL_MS);
    return () => window.clearInterval(interval);
  }, [approvalId, isTerminal, isExpired, status]);

  // Hydrate edit form ONCE when the editor opens. We deliberately do NOT
  // include `approval` in the deps — re-hydrating on every server poll would
  // clobber the user's in-flight edits. Keyed by `editOpen` + `approvalId`.
  useEffect(() => {
    if (!editOpen) return;
    if (!approval) return;
    const expiresInDays = (() => {
      if (!approval.expires_at) return 7;
      const ms = new Date(approval.expires_at).getTime() - Date.now();
      if (!Number.isFinite(ms)) return 7;
      return Math.max(1, Math.min(30, Math.ceil(ms / (24 * 60 * 60 * 1000))));
    })();
    setEditForm({
      email_subject: approval.email_subject || '',
      email_body: approval.email_body || '',
      expires_in_days: expiresInDays,
      provider_name: approval.provider_name || '',
      provider_npi: approval.provider_npi || '',
      provider_email: approval.provider_email || '',
    });
    setEditError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOpen, approvalId]);

  const setEditField = useCallback((name, value) => {
    setEditForm((prev) => (prev ? { ...prev, [name]: value } : prev));
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!approvalId || !editForm) return;
    setEditSubmitting(true);
    setEditError(null);
    try {
      const payload = {
        email_subject: editForm.email_subject,
        email_body: editForm.email_body,
        expires_in_days: Number(editForm.expires_in_days),
        provider_name: editForm.provider_name?.trim() || '',
        provider_npi: editForm.provider_npi || '',
        provider_email: editForm.provider_email?.trim() || '',
      };
      const res = await patchApproval(approvalId, payload);
      if (res?.data) setApproval(res.data);
      setSnackbar({ severity: 'success', message: 'Draft updated.' });
      setEditOpen(false);
    } catch (err) {
      setEditError(extractErrorMessage(err));
    } finally {
      setEditSubmitting(false);
    }
  }, [approvalId, editForm]);

  const openResend = useCallback(() => {
    setResendError(null);
    setResendOpen(true);
  }, []);

  const closeResend = useCallback(() => {
    setResendOpen(false);
    setResendError(null);
    // Return focus to trigger button on next tick (Dialog handles its own
    // focus restoration, but we set our ref explicitly as a safety net).
    window.setTimeout(() => {
      if (resendButtonRef.current) resendButtonRef.current.focus();
    }, 0);
  }, []);

  const handleSendOrResend = useCallback(async () => {
    if (!approvalId) return;
    setResendSubmitting(true);
    setResendError(null);
    try {
      // Draft approvals use the /send/ endpoint; sent/viewed approvals use /resend/
      // which rotates the token server-side.
      if (approval?.status === 'sent' || approval?.status === 'viewed') {
        await resendApproval(approvalId);
      } else {
        await sendApproval(approvalId);
      }
      setResendOpen(false);
      setSnackbar({
        severity: 'success',
        message: approval?.provider_email
          ? `Email queued to ${approval.provider_email}.`
          : 'Email queued.',
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setResendError(extractErrorMessage(err));
    } finally {
      setResendSubmitting(false);
    }
  }, [approvalId, approval?.status, approval?.provider_email]);

  if (loading && !approval) {
    return (
      <Box sx={{ bgcolor: '#F6F7FB', minHeight: '100vh' }}>
        <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress size={28} />
        </Container>
      </Box>
    );
  }

  if (error && !approval) {
    return (
      <Box sx={{ bgcolor: '#F6F7FB', minHeight: '100vh' }}>
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Alert
            severity={error === 'Approval not found.' ? 'info' : 'error'}
            action={
              <Button color="inherit" size="small" component={RouterLink} to="/approvals">
                Back to approvals
              </Button>
            }
          >
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!approval) return null;

  const canSendInitial = approval.status === 'draft' && !isExpired;
  const canResend = (approval.status === 'sent' || approval.status === 'viewed') && !isExpired;
  const sendButtonLabel = canSendInitial ? 'Send to provider' : 'Resend';

  return (
    <Box sx={{ bgcolor: '#F6F7FB', minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Breadcrumbs sx={{ mb: 1.5 }} aria-label="breadcrumb">
          <MuiLink component={RouterLink} to="/approvals" underline="hover" color="inherit">
            Approvals
          </MuiLink>
          <Typography color="text.primary">
            {approval.patient_name || 'Approval'}
          </Typography>
        </Breadcrumbs>

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-end' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
                {approval.patient_name || 'Approval'}
              </Typography>
              <ApprovalStatusChip status={isExpired ? 'expired' : approval.status} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Approval requested from {approval.provider_name}.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/approvals"
            >
              Back
            </Button>
            {canSendInitial && (
              <Button
                variant="outlined"
                onClick={() => setEditOpen((v) => !v)}
                disabled={resendSubmitting || editSubmitting}
              >
                {editOpen ? 'Close editor' : 'Edit'}
              </Button>
            )}
            {(canSendInitial || canResend) && (
              <Button
                variant="contained"
                disableElevation
                onClick={canSendInitial ? handleSendOrResend : openResend}
                disabled={resendSubmitting}
                ref={resendButtonRef}
              >
                {sendButtonLabel}
              </Button>
            )}
          </Stack>
        </Stack>

        {!canSendInitial && approval.status !== 'draft' && (
          <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
            This approval has been sent and can no longer be edited. The secure link was emailed directly to the provider and cannot be retrieved from this screen — use Resend to rotate the link.
          </Alert>
        )}

        {error && approval && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ApprovalTimeline approval={approval} />
          </Grid>

          <Grid item xs={12} md={7}>
            {canSendInitial && editOpen && editForm && (
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: '#FFFFFF',
                  mb: 3,
                }}
              >
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Edit draft
                  </Typography>
                </Box>
                <Box sx={{ px: 2.5, py: 2 }}>
                  {editError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError(null)}>
                      {editError}
                    </Alert>
                  )}
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Provider name"
                        value={editForm.provider_name}
                        onChange={(e) => setEditField('provider_name', e.target.value)}
                        size="small"
                        fullWidth
                        disabled={editSubmitting}
                      />
                      <TextField
                        label="Provider NPI"
                        value={editForm.provider_npi}
                        onChange={(e) =>
                          setEditField('provider_npi', e.target.value.replace(/\D/g, '').slice(0, 10))
                        }
                        size="small"
                        fullWidth
                        disabled={editSubmitting}
                        inputProps={{ inputMode: 'numeric', pattern: '\\d{10}', style: MONO }}
                        helperText="10 digits."
                      />
                    </Stack>
                    <TextField
                      label="Provider email"
                      type="email"
                      value={editForm.provider_email}
                      onChange={(e) => setEditField('provider_email', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={editSubmitting}
                    />
                    <TextField
                      label="Expires in (days)"
                      type="number"
                      value={editForm.expires_in_days}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setEditField(
                          'expires_in_days',
                          Number.isFinite(v) ? Math.min(30, Math.max(1, v)) : 7,
                        );
                      }}
                      size="small"
                      sx={{ maxWidth: 200 }}
                      inputProps={{ min: 1, max: 30 }}
                      disabled={editSubmitting}
                      helperText="1–30."
                    />
                    <TextField
                      label="Subject"
                      value={editForm.email_subject}
                      onChange={(e) => setEditField('email_subject', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={editSubmitting}
                    />
                    <TextField
                      label="Body"
                      value={editForm.email_body}
                      onChange={(e) => setEditField('email_body', e.target.value)}
                      multiline
                      minRows={6}
                      size="small"
                      fullWidth
                      disabled={editSubmitting}
                      helperText="Local edits are the source of truth — server polls will not overwrite unsaved changes."
                    />
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                      <Button
                        variant="text"
                        onClick={() => setEditOpen(false)}
                        disabled={editSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        disableElevation
                        onClick={handleEditSave}
                        disabled={editSubmitting}
                        startIcon={
                          editSubmitting ? <CircularProgress size={14} color="inherit" /> : null
                        }
                      >
                        {editSubmitting ? 'Saving…' : 'Save changes'}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            )}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#FFFFFF' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Email contents
                </Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 2 }}>
                <Field label="Subject">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {approval.email_subject || '—'}
                  </Typography>
                </Field>
                <Divider sx={{ my: 2 }} />
                <Field label="Body">
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                    {approval.email_body || '—'}
                  </Typography>
                </Field>
              </Box>
            </Paper>

            {approval.status === 'declined' && approval.decline_reason && (
              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  border: '1px solid #FECDCA',
                  bgcolor: '#FEF3F2',
                  borderRadius: 1,
                  px: 2.5,
                  py: 2,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#B42318', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Declined
                </Typography>
                {approval.decline_kind && (
                  <Typography variant="body2" sx={{ mt: 0.5, color: '#B42318', fontWeight: 600 }}>
                    {approval.decline_kind}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 0.5, color: '#7A271A' }}>
                  {approval.decline_reason}
                </Typography>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#FFFFFF', px: 2.5, py: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Provider
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Field label="Name">
                    <Typography variant="body2">{approval.provider_name || '—'}</Typography>
                  </Field>
                  <Field label="NPI">
                    <Typography variant="body2" sx={MONO}>{approval.provider_npi || '—'}</Typography>
                  </Field>
                  <Field label="Email">
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{approval.provider_email || '—'}</Typography>
                  </Field>
                  <Field label="Identity check">
                    <Typography variant="body2">
                      {approval.identity_check_mode === 'npi_last4' ? 'NPI last 4' : 'Magic link'}
                    </Typography>
                  </Field>
                  <Field label="Expires">
                    <Typography variant="body2">{formatStamp(approval.expires_at)}</Typography>
                  </Field>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#FFFFFF' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Attached documents ({(approval.documents || []).length})
                  </Typography>
                </Box>
                {(approval.documents || []).length === 0 ? (
                  <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No documents attached.
                    </Typography>
                  </Box>
                ) : (
                  <List dense sx={{ py: 0 }}>
                    {approval.documents.map((d, i) => (
                      <ListItem
                        key={d.id || d.document_id || i}
                        divider={i < approval.documents.length - 1}
                        sx={{ py: 1.25 }}
                      >
                        <ListItemText
                          primary={d.filename || d.document_name || d.name || 'Document'}
                          primaryTypographyProps={{ variant: 'body2', sx: { wordBreak: 'break-word' } }}
                          secondary={d.uploaded_at ? `Uploaded ${formatStamp(d.uploaded_at)}` : null}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <ResendConfirmDialog
        open={resendOpen}
        onClose={closeResend}
        onConfirm={handleSendOrResend}
        submitting={resendSubmitting}
        errorMessage={resendError}
        providerEmail={approval.provider_email}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled">
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

const Field = ({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 92, fontWeight: 600, pt: '2px' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

export default ApprovalDetailPage;
